<template>
	<div :class="style" @click="clickItem()">
		<img
			class="icon"
			v-if="!cardData.variations"
			:src="`/img/items/${cardType}/${cardData.index}.png`"
		/>
		<div class="icon" v-else>
			<img
				loading="lazy"
				v-for="(variation, index) in Object.keys(cardData.variations)"
				:key="variation"
				:class="{ selected: index === selected }"
				:src="`/img/items/${cardType}/${cardData.index}-${variation}.png`"
			/>
			<button
				v-for="(variation, index) in Object.keys(cardData.variations)"
				:key="variation"
				v-on:click.stop="clickVariation($event, index)"
			>
				{{ index }}
			</button>
		</div>
		<span :data-index="cardData.index" class="name">{{ cardData.name }}</span>
		<span class="source" v-if="cardData.source">
			{{ cardData.source }} {{ cardData.sourceNotes }}
		</span>
		<span class="size" v-if="cardData.size">
			{{ cardData.size }}
		</span>
		<span class="speed" v-if="cardData.speed">
			{{ cardData.speed }}
		</span>
		<span class="value" v-if="cardData.value">
			{{ cardData.value }}
		</span>
		<hour-calendar v-if="cardData.hoursBy" v-bind="hours" />
		<month-calendar v-if="cardData.availableBy" v-bind="months" />
	</div>
</template>

<script>
import HourCalendar from '@/components/hour-calendar';
import MonthCalendar from '@/components/month-calendar';

export default {
	name: 'Card',
	components: { MonthCalendar, HourCalendar },
	props: ['cardType', 'cardData'],
	data: () => ({
		selected: 0,
	}),
	computed: {
		hours: function () {
			return this.cardData.hoursBy
				? this.cardData.hoursBy.reduce(
						(p, c) => ({ ...p, [`data-hour${c}`]: true }),
						{}
				  )
				: {};
		},
		months: function () {
			return this.cardData.availableBy
				? this.cardData.availableBy.reduce(
						(p, c) => ({ ...p, [`data-month${c}`]: true }),
						{}
				  )
				: {};
		},
		style: function () {
			return {
				card: true,
				[this.cardType]: true,
				[this.cardData.group]: true,
				hidden: this.cardData.hidden,
				donated:
					this.cardData.variations && this.cardData.donated
						? this.cardData.donated.indexOf(
								Object.keys(this.cardData.variations)[this.selected]
						  ) !== -1
						: this.cardData.donated,
			};
		},
	},
	methods: {
		clickVariation: function (event, index) {
			this.selected = index;
			event.preventDefault();
		},
		clickItem: function () {
			this.$emit('click-item', {
				itemType: this.cardType,
				item: {
					...this.cardData,
					index: this.cardData.variations
						? `${this.cardData.index}:${
								Object.keys(this.cardData.variations)[this.selected]
						  }`
						: this.cardData.index,
				},
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@keyframes fade_in_show {
	0% {
		opacity: 0;
		transform: scale(0);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
}
@keyframes fade_in_hide {
	0% {
		display: block;
		opacity: 1;
	}
	99% {
		display: block;
		position: relative;
	}
	100% {
		display: none;
		opacity: 0;
		position: absolute;
	}
}
.card {
	width: 140px;
	outline: 5px solid #eee;
	background: #eee;
	border: 4px double #7d6f4a52;
	padding: 3px 6px;
	box-shadow: 5px 5px 5px 0 rgba(0, 0, 0, 0.25);
	color: #888;
	font-weight: bold;
	margin: 10px;
	display: inline-block;
	position: relative;
	animation: fade_in_show 0.5s;

	&.hidden {
		animation: fade_in_hide 0.5s;
		animation-fill-mode: forwards;
		pointer-events: none;
	}

	&.bug,
	&.fish,
	&.sea {
		img.icon {
			width: 64px;
			height: 64px;
		}

		.name::before,
		.name::before,
		.name::before {
			display: block;
		}
	}

	div.icon img {
		display: none;
	}
	div.icon img.selected,
	img.icon {
		display: block;
	}

	img.icon,
	div.icon img {
		width: 128px;
		height: 128px;
		pointer-events: none;
		margin: 0 auto;
	}

	.name::before {
		content: attr(data-index);
		color: var(--color-brown);
		left: 2px;
		top: -2px;
		position: absolute;
		text-shadow: 1px 1px var(--color-beige-light);
		display: none;
	}

	span {
		display: block;
		text-align: center;
	}

	.value::before {
		content: ' ';
		display: inline-block;
		background: url(../assets/img/bell-bag.svg) no-repeat;
		filter: invert(43%) sepia(33%) saturate(451%) hue-rotate(6deg)
			brightness(92%) contrast(85%);
		background-size: 12px;
		width: 12px;
		height: 14px;
		vertical-align: middle;
		margin-right: 4px;
	}

	&.donated {
		outline-color: var(--color-beige-lighter);
		background: var(--color-beige-lighter);
		color: var(--color-brown);

		&::after {
			content: 'donated';
			display: inline-block;
			width: auto;
			height: 18px;
			position: absolute;
			bottom: 1px;
			color: white;
			padding: 2px 7px 5px 26px;
			border-radius: 18px;
			background: var(--color-brown);
			right: -19px;
			transform: rotate(-27deg);
		}
	}
	&.donated::before {
		content: ' ';
		position: absolute;
		background: transparent url(../assets/img/owl.svg) no-repeat 0 0;
		background-size: 13px;
		filter: invert(1);
		width: 16px;
		height: 19px;
		display: block;
		transform: rotate(-27deg);
		bottom: -10px;
		right: 43px;
		z-index: 1;
	}

	&.music.donated::after {
		content: 'registered';
		background: var(--color-blue-dark);
	}
	&.music.donated::before {
		content: ' ';
		position: absolute;
		background: transparent url(../assets/img/music.svg) no-repeat 0 0;
		background-size: 13px;
		filter: invert(1);
		width: 13px;
		height: 17px;
		display: block;
		transform: rotate(-27deg);
		bottom: -12px;
		right: 56px;
		z-index: 1;
	}

	&.wardrobe.donated::after {
		content: 'collected';
		background: palevioletred;
	}
	&.wardrobe.donated::before {
		content: ' ';
		position: absolute;
		background: transparent url(../assets/img/tshirt.svg) no-repeat 0 0;
		background-size: 18px;
		filter: invert(1);
		width: 20px;
		height: 16px;
		display: block;
		transform: rotate(-27deg);
		bottom: -10px;
		right: 44px;
		z-index: 1;
	}
}
</style>
